import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Search, User, LogOut } from "lucide-react";
import { ThemeToggle } from "@/components/ds/theme-toggle";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const Header = () => {
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [commandOpen, setCommandOpen] = useState(false);

  const navItems = [
    { label: t("nav.dashboard"), href: "/dashboard" },
    { label: t("nav.calendar"), href: "/dashboard/calendar" },
    { label: t("nav.customers"), href: "/dashboard/customers" },
    { label: t("nav.technicians"), href: "/dashboard/technicians" },
    { label: t("nav.services"), href: "/dashboard/services" },
    { label: t("nav.equipment"), href: "/dashboard/equipment" },
    { label: t("nav.liveTracking"), href: "/dashboard/location" },
    { label: t("nav.profile"), href: "/dashboard/profile" },
  ];

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: t("auth.signOutFailed"),
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: t("auth.signedOut"),
        description: t("auth.signedOutDescription")
      });
    }
  };

  const userInitials = user?.email ?
    user.email.substring(0, 2).toUpperCase() :
    'U';

  return (
    <>
      <header className="h-16 bg-card/80 backdrop-blur-xl border-b border-border px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            className="hidden md:flex items-center gap-2 w-64 justify-start text-muted-foreground border-border bg-secondary/50 hover:bg-secondary hover:text-foreground/70"
            onClick={() => setCommandOpen(true)}
          >
            <Search className="h-4 w-4" />
            <span className="text-sm">{t("actions.search")}</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-secondary/50 px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <LanguageSwitcher />
          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                aria-label={t("nav.accountMenu")}
                className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 p-0"
              >
                <span className="text-sm font-medium text-white">{userInitials}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                <User className="mr-2 h-4 w-4" />
                {t("nav.profile")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                {t("actions.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <CommandInput placeholder={t("search.placeholder")} />
        <CommandList>
          <CommandEmpty>{t("search.empty")}</CommandEmpty>
          <CommandGroup heading={t("search.navigation")}>
            {navItems.map((item) => (
              <CommandItem
                key={item.href}
                onSelect={() => {
                  navigate(item.href);
                  setCommandOpen(false);
                }}
              >
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};
